import { createTransform } from 'redux-persist';
import isPlainObject from 'lodash/isPlainObject';

export type WhitelistValue =
	| boolean
	| ((item: unknown) => boolean)
	| {
			[key: string]: WhitelistValue;
	  };

export type Whitelist = {
	[key: string]: WhitelistValue;
};

export const buildStateRecursiveFromWhitelist = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	stateSlice: Record<any, any>,
	whitelist: Whitelist,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	outputSlice: Record<any, any>,
) => {
	if (!isPlainObject(stateSlice)) {
		throw new Error('buildStateRecursiveFromWhitelist called on stateSlice that is not an object');
	}
	for (const [key, value] of Object.entries(stateSlice)) {
		const filterRule = Object.prototype.hasOwnProperty.call(whitelist, key)
			? whitelist[key]
			: whitelist['*'];
		if (typeof filterRule === 'function') {
			if (filterRule(value)) {
				// eslint-disable-next-line no-param-reassign
				outputSlice[key] = value;
			}
		} else if (filterRule === true) {
			// eslint-disable-next-line no-param-reassign
			outputSlice[key] = value;
		} else if (filterRule !== false && isPlainObject(filterRule) && isPlainObject(value)) {
			// eslint-disable-next-line no-param-reassign
			outputSlice[key] = {};
			buildStateRecursiveFromWhitelist(value, filterRule, outputSlice[key]);
		}
		// false or no entry for this key - do nothing
	}
};

/**
 * createWhitelist
 *
 * Example usage:
 *
 * reducer shape
 * ```
 * {
 *   topLevelKey1: {
 *     one: 'two',
 *     three: {
 *       four: 'five',
 *       six: 'seven
 *     },
 *     eight: 'nine'
 *   },
 *   topLevelKey2: {
 *     arbitraryKeyWithPredictableShape1: {
 *       abc: 'def',
 *       ghi: 'jkl'
 *     },
 *     arbitraryKeyWithPredictableShape2: {
 *       abc: '123',
 *       ghi: '456'
 *     }
 *   }
 * }
 * ```
 *
 * Say we want to store one, three and four from `topLevelKey1`
 * ```
 * const key1Whitelist = createWhitelist(['topLevelKey1'], {
 *   one: true,
 *   three: {
 *     four: true
 *   }
 * })
 * ```
 * (we could explicitly set six and eight to `false` but leaving them out works too)
 *
 * And say we want to store abc from every arbitrary key under topLevelKey2
 * ```
 * const key2Whitelist = createWhitelist(['topLevelKey2'], {
 *   '*': { abc: true }
 * })
 * ```
 *
 * Then apply the transforms in persistReducer options
 * ```
 * persistReducer({ transforms: [key1Whitelist, key2Whitelist]}, reducer)
 * ```
 *
 * Note: persistReducer options should also include a whitelist property that lists all the keys for which you have created whitelist functions
 *
 * @param {string[]} topLevelKeys Which top-level keys in your reducer do you want to apply this whitelist to?
 * @param {Whitelist} whitelistMap An object that maps to the shape of your reducer. See the `Whitelist` type.
 * @return {function} Filter function for use in persistReducer config object under transforms key
 */
export const createWhitelist = (topLevelKeys: string[], whitelistMap: Whitelist) =>
	createTransform(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(inboundState: any) => {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const stateToKeep: Record<string, any> = {};

			buildStateRecursiveFromWhitelist(inboundState, whitelistMap, stateToKeep);

			return stateToKeep;
		},
		(outboundState) => outboundState,
		{ whitelist: topLevelKeys },
	);
